import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  AutomationStatus,
  IAutomationTemplate,
  IExperience
} from '@dadohr/shared/domainTypes/experience';
import { assertNever } from 'assert-never';
import { partition } from 'lodash';
import shortid from 'shortid';

export const getEmptyAutomationTemplate = (
  start: number = 0
): IAutomationTemplate => {
  return {
    id: shortid(),
    title: 'Unnamed Automation',
    description: {
      t: '',
      v: 1
    },
    actions: [],
    audiences: [
      {
        id: shortid(),
        rule: 'EVERYONE',
        name: '',
        color: '',
        blocks: []
      }
    ],
    at: {
      type: 'ABSOLUTE',
      start,
      startTime: null
    },
    timing: {
      type: 'fixed',
      start,
      time: null
    },
    catchup: { disabled: false }
  };
};

export type AutomationStats = {
  total: number;
  done: number;
  error: number;
  upcoming: number;
  skipped: number;
};

export const EMPTY_AUTOMATION_STATS = (): AutomationStats => ({
  total: 0,
  done: 0,
  error: 0,
  upcoming: 0,
  skipped: 0
});

export const getAutomationsStats = (
  instances: Doc<IExperience>[]
): { [automationTplId: string]: AutomationStats } => {
  const s: { [automationTplId: string]: AutomationStats } = {};
  instances.forEach((instance) => {
    const { automations } = instance.data;
    Object.values(automations).forEach((t) => {
      const c = (s[t.templateId] = s[t.templateId] || EMPTY_AUTOMATION_STATS());
      c.total++;
      if (t.status === AutomationStatus.DONE) {
        c.done++;
        return;
      }
      if (t.status === AutomationStatus.ERROR) {
        c.error++;
        return;
      }
      if (t.status === AutomationStatus.UPCOMING) {
        c.upcoming++;
        return;
      }
      if (t.status === AutomationStatus.SKIPPED) {
        c.skipped++;
        return;
      }

      if (t.status === AutomationStatus.TRIGGERED) {
        return;
      }

      assertNever(t.status);
    });
  });
  return s;
};

export const getInstancesThatHaveThisAutomation = (
  instances: Doc<IExperience>[],
  autoTplId: string
) => {
  const [instancesThatHaveThisAutomation] = partition(instances, (instance) => {
    return !!instance.data.automations[autoTplId]; // if this is empty -> then they don't receive the automation. They are not part of an audience that gets this
  });

  return instancesThatHaveThisAutomation; // To be updated if we need instances that are not part of the audience
};
